import { styled } from 'styled-components';

import { Body } from 'components/Text/Body';
import { RichText } from 'components/Text/RichText';
import {
  colors,
  cssBreakpoints,
  gutterWidth,
  maxWidth,
  spacing,
  textStyles,
} from 'theme/theme';
import { imageUrl } from 'utils/filestack';
import { hiDPI } from 'utils/style';

export const Container = styled.div<{
  $imageHandle: string | undefined;
  $mimetype: string | undefined;
}>`
  background-size: cover;
  background-attachment: fixed;
  background-position: center;

  ${({ $imageHandle, $mimetype }) =>
    $imageHandle
      ? `
    background-image: url('${imageUrl({
      data: { handle: $imageHandle, mimetype: $mimetype },
      width: 800,
      height: undefined,
      fit: 'max',
      insecureSVG: false,
    })}');
    ${hiDPI()}: {
      background-image: url('${imageUrl({
        data: { handle: $imageHandle, mimetype: $mimetype },
        width: 1600,
        height: undefined,
        fit: 'max',
        insecureSVG: false,
      })}');
    }
    @media all and (min-width: ${cssBreakpoints.lgUp}) {
      background-image: url('${imageUrl({
        data: { handle: $imageHandle, mimetype: $mimetype },
        width: 1280,
        height: undefined,
        fit: 'max',
        insecureSVG: false,
      })}');
      ${hiDPI()}: {
        background-image: url('${imageUrl({
          data: { handle: $imageHandle, mimetype: $mimetype },
          width: 2560,
          height: undefined,
          fit: 'max',
          insecureSVG: false,
        })}');
      }
    }
  `
      : `background: ${colors.brandBlue};`}

  display: flex;
  justify-content: center;
`;

export const Content = styled.div`
  padding: 0;
  max-width: ${maxWidth[3]}px;
  flex: 1;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    padding: ${spacing[24]};
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: ${gutterWidth}px;
    & > * {
      width: 100%;
      max-width: calc(50% + ${gutterWidth * 2}px);
      margin-bottom: unset;
    }
  }
`;

// NOTE: values taken from CmsDonateBlock
export const Panel = styled.div`
  width: 100%;
  padding: ${spacing[24]};
  box-shadow: 0 0 6px rgb(0 0 0 / 30%);
  background-color: ${colors.white};
  border-radius: 0;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    border-radius: 12px;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: ${gutterWidth}px;
  }
`;

export const Headline = styled(
  ({
    text,
    isPageHeader,
    ...props
  }: {
    text: string;
    isPageHeader: boolean;
  }) => (
    <RichText
      dangerouslySetInnerHTML={{
        __html: `<${isPageHeader ? 'h1' : 'h2'}>${
          text ? text.replace(/<(\/?)(p|h1|h2)>/g, '<$1span>') : ''
        }</${isPageHeader ? 'h1' : 'h2'}>`,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  ),
)`
  && {
    margin-bottom: ${spacing[12]};
    h1,
    h2 {
      /* block-no-empty */
      ${textStyles.h2};
    }
  }
`;

export const HeadlineBody = styled(Body)`
  margin-block: ${spacing[12]} ${spacing[30]};
  border-block-end: 1px solid ${colors.selectionGrey};
  p {
    margin-block-end: ${spacing[24]};
  }
`;

const NoteSectionBase = styled(Body)`
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: 1em;
  margin-block-end: ${spacing[30]};
  padding-block-end: ${spacing[30]};
  border-block-end: 1px solid ${colors.selectionGrey};
`;
export function NoteSection({ label, text }: { label: string; text: string }) {
  return (
    <NoteSectionBase>
      <span>{label}</span>
      <span>{text}</span>
    </NoteSectionBase>
  );
}

export const Summary = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    max-width: 77%;
    aspect-ratio: 409 / 281;
    margin-block-end: ${spacing[30]};
    align-self: center;
    translate: 1.5% 0;
  }
`;

export function SummaryHeadline({ text }: { text: string }) {
  return <Headline isPageHeader={false} text={text} />;
}
